import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import TenantWizard, { TenantWizardData } from '@/components/TenantWizard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-master/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@easyflow/ui-master/components/ui/dialog';
import { 
  Building2, 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  Power,
  PowerOff,
  Copy,
  Check,
  AlertCircle,
  Users,
  Phone,
  Store,
  LogIn,
  Settings,
  BarChart3,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react';
import { impersonationService } from '@/services/impersonation';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { PRODUCT_REGISTRY, DEFAULT_PRODUCT, getProductDefinition } from '@easyflow/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { buildDefaultSipDomain } from '@easyflow/shared';
import { resolveTenantOrigin, useProductOrigins } from '@/lib/productOrigins';
import ProductSystemEmails from '@/components/ProductSystemEmails';
import { TenantLimitsModal } from '@/components/TenantLimitsModal';
import { ChannelAnalyticsModal } from '@/components/ChannelAnalyticsModal';

interface ProvisioningStep {
  status: 'completed' | 'failed';
  timestamp: string;
  error?: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain: string;
  sip_domain: string;
  status: 'active' | 'suspended' | 'pending';
  // Task #3148 — prodotto/edition (tenants.product).
  product?: string;
  companies?: Array<Record<string, unknown>> | null;
  user_count?: number;
  extension_count?: number;
  store_count?: number;
  provisioning_steps?: Record<string, ProvisioningStep> | null;
  created_at: string;
  updated_at: string;
}

function errText(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

function backendErrMsg(error: unknown): string | undefined {
  const e = error as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string } | null;
  return e?.response?.data?.error?.message || e?.response?.data?.message || e?.message;
}


export default function SuperAdminTenants() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Task #3148 — filtro prodotto ('all' | key da PRODUCT_REGISTRY).
  const [productFilter, setProductFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [showChannelAnalytics, setShowChannelAnalytics] = useState(false);
  const [retryingStep, setRetryingStep] = useState<string | null>(null);
  const [provisioningModalTenant, setProvisioningModalTenant] = useState<Tenant | null>(null);
  // Task #3260 — origin del tenant product-aware (fallback dominio piattaforma).
  const tenantOriginFor = useProductOrigins();

  useEffect(() => {
    void loadTenants();
  }, []);

  // Check for ?action=create in URL and open modal
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      setShowCreateModal(true);
      // Remove the query param after opening modal
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadTenants = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/master/tenants');
      // API returns { success: true, data: {tenants: [...], pagination: {...}} }
      // Extract tenants array from response.data object and filter out master tenant
      const tenantsData: Array<Tenant & { is_master?: boolean }> = (response.data as { tenants?: Array<Tenant & { is_master?: boolean }> } | undefined)?.tenants || [];
      const filteredTenants = tenantsData.filter((t) => !t.is_master);
      setTenants(filteredTenants);
    } catch (error: unknown) {
      console.error('❌ loadTenants error:', error);
      toast({
        title: 'Error',
        description: errText(error) || 'Failed to load tenants',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async (data: TenantWizardData, linkedLeadId?: string) => {
    setCreating(true);
    try {
      const payload = {
        name: data.basic.name,
        slug: data.basic.slug,
        status: data.basic.status,
        product: data.basic.product,
        sip_domain: buildDefaultSipDomain(data.basic.slug),
        domain: buildDefaultSipDomain(data.basic.slug),
        settings: {
          language: data.basic.language,
          timezone: data.basic.timezone
        },
        companies: [{
          legal_name: data.company.legal_name,
          legal_form: data.company.legal_form,
          vat_number: data.company.vat_number,
          fiscal_code: data.company.fiscal_code,
          address: data.company.address,
          city: data.company.city,
          postal_code: data.company.postal_code,
          province: data.company.province,
          country: data.company.country,
          pec: data.company.pec,
          phone: data.company.phone,
          description: data.company.description
        }].filter(c => c.legal_name),
        adminUser: {
          firstName: data.admin.firstName,
          lastName: data.admin.lastName,
          email: data.admin.email,
          password: data.admin.password,
          username: data.admin.username || undefined
        }
      };

      const result = await apiClient.post('/master/tenants', payload);
      const newTenantId = (result.data as { id?: string } | undefined)?.id;

      let linkSuccess = false;
      if (linkedLeadId && newTenantId) {
        try {
          await apiClient.post(`/master/tenants/${newTenantId}/link-lead`, {
            entry_id: linkedLeadId,
          });
          linkSuccess = true;
        } catch (linkError: unknown) {
          console.error('Failed to link lead to tenant:', linkError);
        }
      }

      if (linkedLeadId && !linkSuccess) {
        toast({
          title: 'Tenant creato',
          description: 'Tenant creato, ma il collegamento al lead non è riuscito. Puoi collegarlo manualmente.',
          variant: 'destructive',
        });
      }

      setShowCreateModal(false);

      // Fetch the freshly created tenant to read provisioning_steps and open summary modal
      if (newTenantId) {
        try {
          const tenantResp = await apiClient.get<{ tenant?: Tenant } | Tenant>(`/master/tenants/${newTenantId}`);
          const payload = (tenantResp.data || {}) as { tenant?: Tenant } & Tenant;
          const fresh = payload.tenant || payload;
          setProvisioningModalTenant(fresh);
        } catch (e) {
          setProvisioningModalTenant(result.data as Tenant);
        }
      }

      void loadTenants();
    } catch (error: unknown) {
      const backendMessage = backendErrMsg(error);
      toast({
        title: 'Errore',
        description: backendMessage || 'Impossibile creare il tenant',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const refreshProvisioningModal = async (tenantId: string) => {
    try {
      const tenantResp = await apiClient.get<{ tenant?: Tenant } | Tenant>(`/master/tenants/${tenantId}`);
      const payload = (tenantResp.data || {}) as { tenant?: Tenant } & Tenant;
      const fresh = payload.tenant || payload;
      if (fresh) setProvisioningModalTenant(fresh);
    } catch (e) {
      console.error('Failed to refresh tenant provisioning:', e);
    }
  };

  const handleRetryFromModal = async (tenantId: string, step: string) => {
    const key = `${tenantId}-${step}`;
    setRetryingStep(key);
    try {
      await apiClient.post(`/master/tenants/${tenantId}/retry-provisioning`, { step });
      toast({
        title: 'Successo',
        description: `Step "${step}" completato con successo`,
      });
      await refreshProvisioningModal(tenantId);
      void loadTenants();
    } catch (error: unknown) {
      const backendMessage = backendErrMsg(error);
      toast({
        title: 'Errore',
        description: backendMessage || `Impossibile riprovare lo step "${step}"`,
        variant: 'destructive',
      });
      await refreshProvisioningModal(tenantId);
    } finally {
      setRetryingStep(null);
    }
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    try {
      const newStatus = tenant.status === 'active' ? 'suspended' : 'active';
      await apiClient.put(`/master/tenants/${tenant.id}`, { status: newStatus });
      
      toast({
        title: 'Success',
        description: `Tenant ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`,
      });
      
      void loadTenants();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: errText(error) || 'Failed to update tenant status',
        variant: 'destructive',
      });
    }
  };

  const handleImpersonate = async (tenant: Tenant) => {
    try {
      toast({
        title: 'Accesso in corso...',
        description: `Entrando come admin di ${tenant.name}`,
      });
      
      const response = await impersonationService.impersonate(tenant.id);
      
      const tokenData = response.data as { token?: string } | undefined;
      if (response.success && tokenData?.token) {
        const masterToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        const tenantOrigin = await resolveTenantOrigin(tenant.product);
        let tenantUrl = `${tenantOrigin}/${tenant.slug}/auth/impersonate?token=${encodeURIComponent(tokenData.token)}`;
        if (masterToken) {
          tenantUrl += `&backup=${encodeURIComponent(masterToken)}`;
        }
        toast({
          title: 'Impersonation attiva',
          description: `Ora stai operando come admin di ${tenant.name}`,
        });
        window.location.href = tenantUrl;
      } else {
        throw new Error(response.message || 'Impersonation failed');
      }
    } catch (error: unknown) {
      toast({
        title: 'Errore',
        description: errText(error) || 'Impossibile entrare nel tenant',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!confirm(`Are you sure you want to delete tenant "${tenant.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/master/tenants/${tenant.id}`);
      
      toast({
        title: 'Success',
        description: 'Tenant deleted successfully',
      });
      
      void loadTenants();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: errText(error) || 'Failed to delete tenant',
        variant: 'destructive',
      });
    }
  };

  const handleRetryProvisioning = async (tenantId: string, step: string) => {
    const key = `${tenantId}-${step}`;
    setRetryingStep(key);
    try {
      await apiClient.post(`/master/tenants/${tenantId}/retry-provisioning`, { step });
      toast({
        title: 'Successo',
        description: `Step "${step}" completato con successo`,
      });
      void loadTenants();
    } catch (error: unknown) {
      toast({
        title: 'Errore',
        description: errText(error) || `Impossibile riprovare lo step "${step}"`,
        variant: 'destructive',
      });
    } finally {
      setRetryingStep(null);
    }
  };

  const getProvisioningStatus = (tenant: Tenant): { allOk: boolean; failedSteps: string[] } => {
    const steps = tenant.provisioning_steps;
    if (!steps) return { allOk: true, failedSteps: [] };
    const failedSteps = Object.entries(steps)
      .filter(([, v]) => v.status === 'failed')
      .map(([k]) => k);
    return { allOk: failedSteps.length === 0, failedSteps };
  };

  const copyToClipboard = (text: string, field: string) => {
    void navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };


  const filteredTenants = tenants.filter(tenant => {
    // Task #3148 — filtro per prodotto/edition.
    if (productFilter !== 'all' && (tenant.product ?? DEFAULT_PRODUCT) !== productFilter) {
      return false;
    }
    const search = searchTerm.toLowerCase();
    // Task #1930 — match SU TUTTE le ragioni sociali del tenant, non solo la prima.
    const companies: Array<{ legal_name?: string; vat_number?: string }> = Array.isArray(tenant.companies) ? tenant.companies : [];
    const companyMatch = companies.some(c =>
      (c.legal_name || '').toLowerCase().includes(search) ||
      (c.vat_number || '').toLowerCase().includes(search),
    );
    return tenant.name.toLowerCase().includes(search) ||
      tenant.slug.toLowerCase().includes(search) ||
      tenant.domain.toLowerCase().includes(search) ||
      companyMatch;
  });

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === 'active').length,
    suspended: tenants.filter(t => t.status === 'suspended').length,
  };


  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        
        <SkeletonMetricGrid cols={3} />
        
        <SkeletonTable rows={10} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Gestione Organizzazioni
            </h1>
            <p className="text-muted-foreground mt-1">Manage all tenants and their configurations</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <Plus className="mr-2 h-4 w-4" />
            Create Tenant
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <GlassCard className="border-l-4 border-l-blue-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription>Total Tenants</GlassCardDescription>
              <GlassCardTitle className="text-3xl">{stats.total}</GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>
          <GlassCard className="border-l-4 border-l-green-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription>Active Tenants</GlassCardDescription>
              <GlassCardTitle className="text-3xl text-green-600">{stats.active}</GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>
          <GlassCard className="border-l-4 border-l-red-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription>Suspended Tenants</GlassCardDescription>
              <GlassCardTitle className="text-3xl text-red-600">{stats.suspended}</GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>
        </div>
      </div>

      <Tabs defaultValue="tenants">
        <TabsList>
          <TabsTrigger value="tenants">Organizzazioni</TabsTrigger>
          <TabsTrigger value="system-emails">Email di sistema</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-4">
      {/* Search and Table */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 h-4 w-4" />
              <Input
                placeholder="Search by name, slug, or domain..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Prodotto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i prodotti</SelectItem>
                {PRODUCT_REGISTRY.map(p => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Ragione Sociale</TableHead>
                    <TableHead>Login URL</TableHead>
                    <TableHead>SIP Domain</TableHead>
                    <TableHead>Prodotto</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Provisioning</TableHead>
                    <TableHead className="text-center">Users</TableHead>
                    <TableHead className="text-center">Extensions</TableHead>
                    <TableHead className="text-center">Stores</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                        No tenants found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTenants.map((tenant) => (
                      <TableRow key={tenant.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{tenant.name}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted text-foreground rounded text-sm">{tenant.slug}</code>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Task #1930 — mostra la PRIMA ragione sociale per created_at
                            // (l'ordering è già garantito dall'API) e un badge "+N" con
                            // tooltip che elenca le restanti.
                            const cs: Array<{ legal_name?: string; vat_number?: string }> = Array.isArray(tenant.companies) ? tenant.companies : [];
                            if (cs.length === 0) {
                              return <span className="text-xs text-muted-foreground/70 italic">—</span>;
                            }
                            const first = cs[0]!;
                            const rest = cs.slice(1);
                            return (
                              <div className="flex items-center gap-1.5 min-w-0">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate max-w-[180px]" title={first.legal_name || ''}>
                                    {first.legal_name || '—'}
                                  </div>
                                  {first.vat_number && (
                                    <div className="text-[10px] text-muted-foreground font-mono">P.IVA {first.vat_number}</div>
                                  )}
                                </div>
                                {rest.length > 0 && (
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px] flex-shrink-0 cursor-help"
                                    title={rest.map(c => `• ${c.legal_name || '—'}${c.vat_number ? ` (P.IVA ${c.vat_number})` : ''}`).join('\n')}
                                  >
                                    +{rest.length}
                                  </Badge>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{tenantOriginFor(tenant.product).replace(/^https?:\/\//, '')}/{tenant.slug}/login</span>
                            <button
                              onClick={() => copyToClipboard(`${tenantOriginFor(tenant.product)}/${tenant.slug}/login`, `url-${tenant.id}`)}
                              className="text-muted-foreground/70 hover:text-muted-foreground"
                            >
                              {copiedField === `url-${tenant.id}` ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{tenant.sip_domain}</span>
                            <button
                              onClick={() => copyToClipboard(tenant.sip_domain, `sip-${tenant.id}`)}
                              className="text-muted-foreground/70 hover:text-muted-foreground"
                            >
                              {copiedField === `sip-${tenant.id}` ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Task #3148 — badge prodotto da PRODUCT_REGISTRY (mai hardcodato).
                            const key = tenant.product ?? DEFAULT_PRODUCT;
                            const def = getProductDefinition(key);
                            return (
                              <Badge variant="outline" className="whitespace-nowrap">
                                {def ? def.label : key}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={tenant.status === 'active' ? 'default' : tenant.status === 'suspended' ? 'destructive' : 'secondary'}
                          >
                            {tenant.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const { allOk, failedSteps } = getProvisioningStatus(tenant);
                            if (!tenant.provisioning_steps) return <span className="text-xs text-muted-foreground/70">—</span>;
                            if (allOk) {
                              return (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle2 className="h-4 w-4" />
                                  <span className="text-xs">OK</span>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-1">
                                {failedSteps.map((step) => (
                                  <div key={step} className="flex items-center gap-1">
                                    <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                                    <span className="text-xs text-red-600 truncate max-w-[80px]" title={step}>{step}</span>
                                    <button
                                      onClick={() => handleRetryProvisioning(tenant.id, step)}
                                      disabled={retryingStep === `${tenant.id}-${step}`}
                                      className="ml-1 text-blue-500 hover:text-blue-700 disabled:opacity-50"
                                      title={`Riprova ${step}`}
                                    >
                                      {retryingStep === `${tenant.id}-${step}` ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-3 w-3" />
                                      )}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="h-4 w-4 text-muted-foreground/70" />
                            <span>{tenant.user_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Phone className="h-4 w-4 text-muted-foreground/70" />
                            <span>{tenant.extension_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Store className="h-4 w-4 text-muted-foreground/70" />
                            <span>{tenant.store_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/tenants/${tenant.id}`)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowLimitsModal(true);
                              }}
                              title="Limiti tenant"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowChannelAnalytics(true);
                              }}
                              title="Channel Analytics"
                              className="text-green-600 hover:text-green-700"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(tenant)}
                              title="Impersonate Tenant"
                              className="text-purple-600 hover:text-purple-700"
                            >
                              <LogIn className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(tenant)}
                              title={tenant.status === 'active' ? 'Suspend' : 'Activate'}
                            >
                              {tenant.status === 'active' ? (
                                <PowerOff className="h-4 w-4" />
                              ) : (
                                <Power className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTenant(tenant)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Tenant"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>
        </TabsContent>

        <TabsContent value="system-emails" className="mt-4">
          <GlassCard>
            <GlassCardContent className="pt-6">
              <ProductSystemEmails />
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>

      {/* Tenant Wizard Modal */}
      <TenantWizard
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTenant}
        isSubmitting={creating}
      />

      {/* Tenant Limits Modal (VoIP / Users / AI Agents) */}
      <TenantLimitsModal
        open={showLimitsModal}
        onClose={() => {
          setShowLimitsModal(false);
          setSelectedTenant(null);
        }}
        tenant={selectedTenant}
      />

      {/* Channel Analytics Modal */}
      <ChannelAnalyticsModal
        open={showChannelAnalytics}
        onClose={() => {
          setShowChannelAnalytics(false);
          setSelectedTenant(null);
        }}
        tenant={selectedTenant}
      />

      {/* Provisioning Summary Modal */}
      <ProvisioningSummaryModal
        tenant={provisioningModalTenant}
        retryingStep={retryingStep}
        onClose={() => setProvisioningModalTenant(null)}
        onRetry={handleRetryFromModal}
      />
    </div>
  );
}

// ============================================================================
// Provisioning Summary Modal
// ============================================================================

const PROVISIONING_STEPS: { key: string; label: string }[] = [
  { key: 'extensions', label: 'Estensioni predefinite' },
  { key: 'routes', label: 'Rotte standard' },
  { key: 'contexts', label: 'Contesti FreeSWITCH' },
  { key: 'freeswitch_config', label: 'Configurazione FreeSWITCH' },
  { key: 'appointment_automations', label: 'Automazioni appuntamenti' },
  { key: 'ai_agents', label: 'Agenti AI predefiniti' },
  { key: 'storage', label: 'Storage e quote' },
  { key: 'integrations', label: 'Integrazioni predefinite' },
  { key: 'welcome_email', label: 'Email di benvenuto' },
];

interface ProvisioningSummaryModalProps {
  tenant: Tenant | null;
  retryingStep: string | null;
  onClose: () => void;
  onRetry: (tenantId: string, step: string) => void;
}

function ProvisioningSummaryModal({
  tenant,
  retryingStep,
  onClose,
  onRetry,
}: ProvisioningSummaryModalProps) {
  const open = !!tenant;
  const steps = tenant?.provisioning_steps || {};
  const failedCount = Object.values(steps).filter((s) => s?.status === 'failed').length;
  const completedCount = Object.values(steps).filter((s) => s?.status === 'completed').length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {failedCount === 0 ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            Riepilogo provisioning {tenant ? `– ${tenant.name}` : ''}
          </DialogTitle>
          <DialogDescription>
            {failedCount === 0
              ? `Tutti gli step di provisioning sono stati completati (${completedCount}/${PROVISIONING_STEPS.length}).`
              : `${failedCount} step non sono andati a buon fine. Puoi riprovarli singolarmente.`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto divide-y divide-border border rounded-md">
          {PROVISIONING_STEPS.map(({ key, label }) => {
            const step = steps[key];
            const status = step?.status || 'pending';
            const error = step?.error;
            const timestamp = step?.timestamp;
            const retryKey = tenant ? `${tenant.id}-${key}` : '';
            const isRetrying = retryingStep === retryKey;

            return (
              <div key={key} className="flex items-start gap-3 p-3">
                <div className="mt-0.5">
                  {status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                  {status === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}
                  {status === 'pending' && <Loader2 className="h-5 w-5 text-muted-foreground/70" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-foreground">{label}</p>
                    <span className="text-xs text-muted-foreground">
                      {status === 'completed' && 'Completato'}
                      {status === 'failed' && 'Fallito'}
                      {status === 'pending' && 'In attesa'}
                    </span>
                  </div>
                  {timestamp && (
                    <p className="text-xs text-muted-foreground/70 mt-0.5">
                      {new Date(timestamp).toLocaleString()}
                    </p>
                  )}
                  {error && (
                    <p className="text-xs text-red-600 mt-1 break-words" title={error}>
                      {error}
                    </p>
                  )}
                </div>
                {status === 'failed' && tenant && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isRetrying}
                    onClick={() => onRetry(tenant.id, key)}
                    className="shrink-0"
                  >
                    {isRetrying ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Riprovo...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Riprova
                      </>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
